import { useState, useCallback, useRef } from 'react'
import { Conversation } from '@elevenlabs/client'

interface UseElevenLabsConversationOptions {
  getSignedUrl: () => Promise<string>
  voiceId: string
  language: string
  instructions: string
  firstMessage?: string
  onTranscript: (message: { role: 'user' | 'assistant'; content: string }) => void
}

interface UseElevenLabsConversationReturn {
  start: () => Promise<void>
  stop: () => Promise<void>
  isConnected: boolean
  isConnecting: boolean
  isSpeaking: boolean
  /** Mute or unmute the microphone upstream of the live conversation. No-op before `start`. */
  setMicMuted: (muted: boolean) => void
  isMicMuted: boolean
}

export function useElevenLabsConversation(options: UseElevenLabsConversationOptions): UseElevenLabsConversationReturn {
  const [isConnected, setIsConnected] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [isMicMuted, setIsMicMuted] = useState(false)
  const conversationRef = useRef<any>(null)
  const isStartingRef = useRef(false)

  const start = useCallback(async () => {
    if (conversationRef.current || isStartingRef.current) return

    isStartingRef.current = true
    setIsConnecting(true)
    try {
      const signedUrl = await options.getSignedUrl()

      const overrides: any = {}
      if (options.instructions || options.language || options.firstMessage) {
        overrides.agent = {
          ...(options.instructions ? { prompt: { prompt: options.instructions } } : {}),
          ...(options.language ? { language: options.language } : {}),
          ...(options.firstMessage ? { firstMessage: options.firstMessage } : {}),
        }
      }
      if (options.voiceId) {
        overrides.tts = { voiceId: options.voiceId }
      }

      const conversation = await Conversation.startSession({
        signedUrl,
        ...(Object.keys(overrides).length > 0 ? { overrides } : {}),
        onConnect: () => {
          console.log('[ElevenLabs] Connected')
          setIsConnected(true)
          setIsConnecting(false)
        },
        onDisconnect: (details: any) => {
          console.warn('[ElevenLabs] Disconnected:', details?.reason, details?.closeCode, details?.closeReason, details?.message)
          setIsConnected(false)
          setIsSpeaking(false)
          setIsMicMuted(false)
          conversationRef.current = null
          isStartingRef.current = false
        },
        onMessage: (message: any) => {
          if (message.source === 'user' && message.message) {
            options.onTranscript({ role: 'user', content: message.message })
          } else if (message.source === 'ai' && message.message) {
            options.onTranscript({ role: 'assistant', content: message.message })
          }
        },
        onStatusChange: (status: any) => {
          console.log('[ElevenLabs] Status:', status?.status)
        },
        onModeChange: (mode: any) => {
          setIsSpeaking(mode.mode === 'speaking')
        },
        onError: (error: any, context: any) => {
          console.error('[ElevenLabs] Error:', error, context)
          setIsConnecting(false)
        },
      })

      conversationRef.current = conversation
      isStartingRef.current = false
    } catch (error) {
      console.error('Failed to start ElevenLabs conversation:', error)
      isStartingRef.current = false
      setIsConnecting(false)
      throw error
    }
  }, [options.getSignedUrl, options.voiceId, options.language, options.instructions, options.firstMessage, options.onTranscript])

  const stop = useCallback(async () => {
    isStartingRef.current = false
    if (conversationRef.current) {
      await conversationRef.current.endSession()
      conversationRef.current = null
      setIsConnected(false)
      setIsSpeaking(false)
    }
    setIsMicMuted(false)
  }, [])

  const setMicMuted = useCallback((muted: boolean) => {
    conversationRef.current?.setMicMuted?.(muted)
    setIsMicMuted(muted)
  }, [])

  return {
    start,
    stop,
    isConnected,
    isConnecting,
    isSpeaking,
    setMicMuted,
    isMicMuted,
  }
}
